import React, {useCallback, useEffect, useMemo, useState} from "react";
import NiceModal from "@ebay/nice-modal-react";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import PhotoOutlinedIcon from "@mui/icons-material/PhotoOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import UnpublishedOutlinedIcon from "@mui/icons-material/UnpublishedOutlined";
import {GridColDef, GridRowSelectionModel} from "@mui/x-data-grid";
import {useNavigate} from "react-router-dom";
import {toast} from "sonner";
import {Box, Chip, Colors, Text} from "@bounce/shared/ui";
import {
  Button,
  countActiveFilters,
  FilterOption,
  FilterSidebar,
  FiltersToggleButton,
  FilterValues,
  LIST_HEADER_HEIGHT,
  ListDataGrid,
  ListPageHeader,
  RowActions,
  SearchInput,
  SidebarFilter,
  hasActiveFilters,
  sortedByLocale,
} from "../../ui/components";
import AreYouSureDialog from "@bounce/shared/consumer/bounce-ui/components/Dialogs/AreYouSureDialog";
import {useLocalStorageState} from "@bounce/shared/hooks";
import {useBatchDeleteTemplates} from "../../apis/requests/templates/batchDeleteTemplates";
import {useBatchTranslateTemplates} from "../../apis/requests/templates/batchTranslateTemplates";
import {useBatchUpdateTemplateStatus} from "../../apis/requests/templates/batchUpdateTemplateStatus";
import {useDeleteTemplate} from "../../apis/requests/templates/deleteTemplate";
import {getTemplate} from "../../apis/requests/templates/getTemplate";
import {useGetTemplates} from "../../apis/requests/templates/getTemplates";
import {ReachTemplateToggleStatus, useUpdateTemplateStatus} from "../../apis/requests/templates/updateTemplateStatus";
import {
  ReachTemplateContentChannel,
  ReachTemplatesFilters,
  ReachTemplatesRead,
  ReachTemplateStatus,
  ReachTemplateType,
} from "../../apis/requests/templates/types";
import {useGetSegments} from "../../apis/requests/segments/getSegments";
import {SegmentMatchMode} from "../../apis/requests/segments/types";
import {useDebounce} from "../../hooks/useDebounce";
import {useFilterPanel} from "../../hooks/useFilterPanel";
import {FlagES, FlagUS} from "../../assets";
import {reachUrls} from "../../layout/menu-items";
import DeleteTranslationDialog from "./DeleteTranslationDialog";
import {showTemplatesInUse} from "./TemplateInUseDialog";
import {TemplatesBatchActions} from "./TemplatesBatchActions";
import {CHANNEL_DISPLAY, ChipVariant, STATUS_DISPLAY, TYPE_DISPLAY} from "./templateDisplay";

const CHANNEL_ICONS: Record<ReachTemplateContentChannel, React.ReactNode> = {
  email: <MailOutlineRoundedIcon sx={{fontSize: 16, color: Colors.grey._600}} />,
  sms: <SmsOutlinedIcon sx={{fontSize: 16, color: Colors.grey._600}} />,
  sms_with_landing_page: <SmsOutlinedIcon sx={{fontSize: 16, color: Colors.grey._600}} />,
  mms: <PhotoOutlinedIcon sx={{fontSize: 16, color: Colors.grey._600}} />,
  mms_with_landing_page: <PhotoOutlinedIcon sx={{fontSize: 16, color: Colors.grey._600}} />,
  whatsapp: <ChatOutlinedIcon sx={{fontSize: 16, color: Colors.grey._600}} />,
};

const channelFilterOptions: FilterOption[] = Object.entries(CHANNEL_DISPLAY).map(([value, display]) => ({
  value,
  label: display.label,
}));

const statusFilterOptions: FilterOption[] = Object.entries(STATUS_DISPLAY).map(([value, display]) => ({
  value,
  label: display.label,
  chipVariant: display.variant,
}));

const typeFilterOptions: FilterOption[] = Object.entries(TYPE_DISPLAY).map(([value, display]) => ({
  value,
  label: display.label,
  chipVariant: display.variant,
}));

const DEFAULT_SEGMENT_FILTER: FilterOption = {value: "__default__", label: "Default", chipVariant: "inherit"};

const TEMPLATES_FILTERS_STORAGE_KEY = "reach-templates-filters";
const TEMPLATES_SEGMENT_MATCH_MODE_STORAGE_KEY = "reach-templates-segment-match-mode";
const TEMPLATES_FILTER_PANEL_STORAGE_KEY = "reach-templates-filter-panel-expanded";
const HEADER_CONTROL_HEIGHT = 40;
const DEFAULT_PAGE_SIZE = 100;

const toTemplatesFilters = (
  values: FilterValues,
  segmentMatchMode: SegmentMatchMode,
  search: string
): ReachTemplatesFilters => {
  const selectedSegments = values.segments ?? [];
  return {
    segmentIds: selectedSegments.filter((value) => value !== DEFAULT_SEGMENT_FILTER.value).map(Number),
    segmentMatchMode,
    includeUnassignedSegments: selectedSegments.includes(DEFAULT_SEGMENT_FILTER.value),
    contentChannels: (values.channel ?? []) as ReachTemplateContentChannel[],
    statuses: (values.status ?? []) as ReachTemplateStatus[],
    types: (values.type ?? []) as ReachTemplateType[],
    search,
  };
};

const TemplatesPage = () => {
  const navigate = useNavigate();
  const [paginationModel, setPaginationModel] = useState({page: 0, pageSize: DEFAULT_PAGE_SIZE});
  const [filterValues, setFilterValues] = useLocalStorageState<FilterValues>(
    TEMPLATES_FILTERS_STORAGE_KEY,
    {},
    {encrypted: false}
  );
  const [segmentMatchMode, setSegmentMatchMode] = useLocalStorageState<SegmentMatchMode>(
    TEMPLATES_SEGMENT_MATCH_MODE_STORAGE_KEY,
    "any",
    {encrypted: false}
  );
  const filterPanel = useFilterPanel(TEMPLATES_FILTER_PANEL_STORAGE_KEY);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const templatesFilters = useMemo(
    () => toTemplatesFilters(filterValues, segmentMatchMode, debouncedSearch),
    [filterValues, segmentMatchMode, debouncedSearch]
  );
  useEffect(() => {
    setPaginationModel((m) => ({...m, page: 0}));
    setSelectionModel([]);
  }, [debouncedSearch]);

  useEffect(() => {
    setSelectionModel([]);
  }, [filterValues, segmentMatchMode]);
  const {data, isLoading, isFetching, error} = useGetTemplates(
    {paginationModel, filters: templatesFilters},
    {keepPreviousData: true}
  );
  const {data: segments} = useGetSegments();
  const {mutateAsync: deleteAsync} = useDeleteTemplate();
  const {mutateAsync: updateStatusAsync} = useUpdateTemplateStatus();
  const {mutateAsync: batchDeleteAsync} = useBatchDeleteTemplates();
  const {mutateAsync: batchUpdateStatusAsync} = useBatchUpdateTemplateStatus();
  const {mutateAsync: batchTranslateAsync} = useBatchTranslateTemplates();
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);

  const templates = data?.items ?? [];

  const getTemplateUrl = (id: number | string) => `/templates/${id}`;

  const segmentOptions = useMemo<FilterOption[]>(
    () =>
      [...(segments ?? [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((segment) => ({value: String(segment.id), label: segment.name})),
    [segments]
  );

  const filters = useMemo<SidebarFilter[]>(
    () => [
      {
        key: "segments",
        placeholder: "Segments",
        options: [DEFAULT_SEGMENT_FILTER, ...segmentOptions],
        chipVariant: "blue",
        matchMode: {
          value: segmentMatchMode,
          onChange: (mode) => {
            setSegmentMatchMode(mode);
            setPaginationModel((m) => ({...m, page: 0}));
          },
        },
      },
      {
        key: "channel",
        placeholder: "Channel",
        options: channelFilterOptions,
        chipVariant: "blue",
      },
      {
        key: "status",
        placeholder: "Status",
        options: statusFilterOptions,
      },
      {
        key: "type",
        placeholder: "Type",
        options: typeFilterOptions,
      },
    ],
    [segmentOptions, segmentMatchMode, setSegmentMatchMode]
  );

  const hasTranslation = (row: ReachTemplatesRead) => row.languages.length > 1;

  const confirmDeleteTranslations = (templateCount: number, onConfirm: (deleteTranslations: boolean) => void) => {
    NiceModal.show(DeleteTranslationDialog, {templateCount, onConfirm});
  };

  const showInUse = async (action: "delete" | "draft", id: number, name: string) => {
    try {
      const {triggers = []} = await getTemplate({id: String(id)});
      return showTemplatesInUse(action, [{id, name, triggers}]);
    } catch {
      return false;
    }
  };

  const onDelete = async (id: number, templateName: string) => {
    if (await showInUse("delete", id, templateName)) return;
    const row = templates.find((t) => t.id === id);
    NiceModal.show(AreYouSureDialog, {
      onConfirm: () => {
        const deleteAndNotify = async (deleteTranslations: boolean) => {
          await deleteAsync({id: String(id), deleteTranslations});
          toast.success(`Template "${templateName}" deleted`);
        };
        if (row && hasTranslation(row)) {
          confirmDeleteTranslations(1, deleteAndNotify);
        } else {
          deleteAndNotify(true);
        }
      },
      contentTextId: `Are you sure you want to delete "${templateName}"?`,
      confirmTextId: "Delete",
    });
  };

  const onSetStatus = async (id: number, templateName: string, status: ReachTemplateToggleStatus) => {
    if (status === "draft" && (await showInUse("draft", id, templateName))) return;
    try {
      await updateStatusAsync({id: String(id), status});
      toast.success(`Template "${templateName}" is now ${status === "active" ? "active" : "a draft"}`);
    } catch {
      toast.error(`Failed to update "${templateName}"`);
    }
  };

  const clearSelection = useCallback(() => setSelectionModel([]), []);

  const selectedIds = selectionModel as (number | string)[];
  const selectedTemplateIds = selectedIds.map((id) => Number(id));
  const selectedTemplates = templates.filter((t) => selectedTemplateIds.includes(t.id ?? -1));
  const selectedCount = selectedTemplateIds.length;

  const checkboxState: "checked" | "indeterminate" | "unchecked" =
    selectedCount === 0
      ? "unchecked"
      : selectedCount >= templates.length
        ? "checked"
        : "indeterminate";

  const handleToggleAll = () => {
    if (checkboxState === "checked") {
      clearSelection();
    } else {
      setSelectionModel(templates.map((t) => t.id));
    }
  };

  const onBatchDelete = () => {
    NiceModal.show(AreYouSureDialog, {
      onConfirm: () => {
        const deleteAndNotify = async (deleteTranslations: boolean) => {
          await batchDeleteAsync({templateIds: selectedTemplateIds, deleteTranslations});
          toast.success(`${selectedTemplateIds.length} template(s) deleted`);
          setSelectionModel([]);
        };
        if (selectedTemplates.some(hasTranslation)) {
          confirmDeleteTranslations(selectedTemplateIds.length, deleteAndNotify);
        } else {
          deleteAndNotify(true);
        }
      },
      contentTextId: `Are you sure you want to delete ${selectedTemplateIds.length} template(s)?`,
      confirmTextId: "Delete",
    });
  };

  const onBatchActivate = async () => {
    try {
      await batchUpdateStatusAsync({templateIds: selectedTemplateIds, status: "active"});
      toast.success(`${selectedTemplateIds.length} template(s) activated`);
      setSelectionModel([]);
    } catch {
      toast.error("Failed to activate selected templates");
    }
  };

  const onBatchTranslate = async () => {
    try {
      await batchTranslateAsync({templateIds: selectedTemplateIds});
      toast.success(`Translation started for ${selectedTemplateIds.length} template(s)`);
      setSelectionModel([]);
    } catch {
      toast.error("Failed to start translation for selected templates");
    }
  };

  const columns = useMemo<GridColDef<ReachTemplatesRead>[]>(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 2,
        renderCell: ({row}) => (
          <Text
            size={14}
            variant="inter500"
            sx={{color: Colors.grey._900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
          >
            {row.name}
          </Text>
        ),
      },
      {
        field: "content_channel",
        headerName: "Channel",
        flex: 1,
        renderCell: ({row}) => {
          const display = CHANNEL_DISPLAY[row.content_channel] ?? {label: row.content_channel};
          const icon = CHANNEL_ICONS[row.content_channel];
          return (
            <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
              {icon}
              <Text size={13} sx={{color: Colors.grey._800}}>
                {display.label}
              </Text>
            </Box>
          );
        },
      },
      {
        field: "type",
        headerName: "Type",
        flex: 1,
        renderCell: ({row}) => {
          const display = TYPE_DISPLAY[row.type] ?? {label: row.type, variant: "inherit" as ChipVariant};
          return <Chip size_="small" variant_={display.variant} label={display.label} />;
        },
      },
      {
        field: "segments",
        headerName: "Segments",
        flex: 2,
        valueGetter: (_value, row) => sortedByLocale(row.segments.map((s) => s.name)).join(", "),
        renderCell: ({row}) =>
          row.segments.length === 0 ? (
            <Text size={13} sx={{color: Colors.grey._600}}>
              Default
            </Text>
          ) : (
            <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", py: 1}}>
              {row.segments.map((s) => (
                <Chip key={s.id} size_="small" variant_="blue" label={s.name} />
              ))}
            </Box>
          ),
      },
      {
        field: "languages",
        headerName: "Languages",
        flex: 1,
        valueGetter: (_value, row) => row.languages.join(", "),
        renderCell: ({row}) => (
          <Box sx={{display: "flex", gap: 0.75, alignItems: "center"}}>
            {row.languages.map((lang) => (
              <Box key={lang} component={lang === "es" ? FlagES : FlagUS} sx={{width: 18, height: 18}} />
            ))}
          </Box>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        renderCell: ({row}) => {
          const display = STATUS_DISPLAY[row.status] ?? {label: row.status, variant: "inherit" as ChipVariant};
          const dotColor: Record<string, string> = {
            active: Colors.neon._700,
            draft: Colors.grey._400,
            inactive: Colors.orange._600,
          };
          return (
            <Chip
              size_="small"
              variant_={display.variant}
              label={display.label}
              icon={
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    backgroundColor: dotColor[row.status] ?? Colors.grey._400,
                    flexShrink: 0,
                  }}
                />
              }
              sx={{
                "& .MuiChip-icon": {marginLeft: "2px", marginRight: "-2px"},
                "& .MuiChip-label": {paddingLeft: "6px", paddingRight: "2px"},
              }}
            />
          );
        },
      },
      {
        field: "principal",
        headerName: "Created by",
        flex: 1,
        filterable: false,
        renderCell: ({row}) => (
          <Text
            size={14}
            sx={{color: Colors.grey._700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
          >
            {row.principal ? row.principal.split("@")[0] : ""}
          </Text>
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 72,
        sortable: false,
        filterable: false,
        renderCell: ({row}) => (
          <RowActions
            onEdit={() => navigate(getTemplateUrl(row.id))}
            onDelete={() => onDelete(row.id, row.name)}
            onOpenInNewTab={() => window.open(getTemplateUrl(row.id), "_blank")}
            extraActions={[
              row.status === "active"
                ? {
                    label: "Mark as draft",
                    icon: <UnpublishedOutlinedIcon sx={{width: 20, height: 20}} />,
                    onClick: () => onSetStatus(row.id, row.name, "draft"),
                  }
                : {
                    label: "Mark as active",
                    icon: <CheckCircleOutlineIcon sx={{width: 20, height: 20}} />,
                    onClick: () => onSetStatus(row.id, row.name, "active"),
                  },
            ]}
          />
        ),
      },
    ],
    [navigate]
  );

  return (
    <Box sx={{p: 4, display: "flex", flexDirection: "column", height: "100%"}}>
      <ListPageHeader title="Templates">
        <FiltersToggleButton panel={filterPanel} activeCount={countActiveFilters(filterValues)} />
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search templates"
          sx={{height: HEADER_CONTROL_HEIGHT}}
        />
        <Button
          buttonType="contained-primary"
          size="small"
          onClick={() => navigate(reachUrls.templatesNew)}
          sx={{height: HEADER_CONTROL_HEIGHT}}
        >
          + New template
        </Button>
      </ListPageHeader>
      <Box sx={{display: "flex", flex: 1, minHeight: 0}}>
        <FilterSidebar
          filters={filters}
          values={filterValues}
          onChange={(values) => {
            setFilterValues(values);
            setPaginationModel((m) => ({...m, page: 0}));
            clearSelection();
          }}
          panel={filterPanel}
        />
        <Box sx={{display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0}}>
          {error ? (
            <Text size={14}>Failed to load templates.</Text>
          ) : (
            <ListDataGrid
              rows={templates}
              columns={columns}
              loading={isLoading || isFetching}
              getRowId={(row) => row.id}
              initialState={{sorting: {sortModel: [{field: "name", sort: "asc"}]}}}
              getRowUrl={(row) => getTemplateUrl(row.id)}
              paginationMode="server"
              rowCount={data?.total ?? 0}
              paginationModel={paginationModel}
              onPaginationModelChange={(model) => {
                setPaginationModel(model);
                clearSelection();
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              disableColumnMenu={false}
              columnHeaderHeight={LIST_HEADER_HEIGHT}
              checkboxSelection
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={setSelectionModel}
              selectionBar={
                selectedCount > 0 ? (
                  <TemplatesBatchActions
                    selectedCount={selectedCount}
                    checkboxState={checkboxState}
                    onToggleAll={handleToggleAll}
                    onActivate={onBatchActivate}
                    onTranslate={onBatchTranslate}
                    onDelete={onBatchDelete}
                  />
                ) : undefined
              }
              noRowsText={
                debouncedSearch || hasActiveFilters(filterValues)
                  ? "No templates match your filters"
                  : "No templates yet"
              }
              sx={{
                '& .MuiDataGrid-cell[data-field="name"], & .MuiDataGrid-columnHeader[data-field="name"]': {pl: 1.5},
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TemplatesPage;
